import { DropdownButtonType, InlineAlertType, InlineAlertWidget, ListItem, LocallyFilteringList, RadioButtonWithLabelOption, SingleSelectDropdown, TextLinkWidget } from '@tableau/widgets';
import { RadioButtonWithLabelGroupWidget } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import React from 'react';
import { classes, style } from 'typestyle';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~commonInfra/services/HelpService';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { dropdownListStyle, learnMoreStyle, settingsRadioButtonItemHeight, SettingsStyles } from '~src/settings/styles/CommonStyles';
import { Groups } from '~src/userManagement/groups/Groups';
import { IGroupInfo } from '~src/userManagement/groups/models/IGroupInfo';

import { SessionServerSelectors, SessionSiteSelectors } from '../../../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { useSelector } from '../../../utils';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';

const subSettingsWrapper = style({
  display: 'flex',
});

export const ALLOW_PAT_CREATION_CHECKBOX = 'allow-pat-creation-checkbox';
export const ALLOW_PAT_CREATION_SITE_SETTING = 'settings.allowPatCreation';
export const ALLOW_PAT_GROUP_SITE_SETTING = 'settings.allowPatGroup';
export const PAT_ABSOLUTE_EXPIRY_SITE_SETTING = 'settings.patAbsoluteExpiry';

export const ALLOW_PAT_DEFAULT_GROUP: String = undefined; //use undefined instead of null as required by the component
export const PAT_DEFAULT_EXPIRY = 180;
export const PAT_EXPIRY_MIN_RANGE_VALUE = 1;
export const PAT_EXPIRY_MAX_RANGE_VALUE = 365;

// Test IDs
export const ALLOW_PAT_CREATION_SITE_SETTING_TEST_ID = 'allow-pat-creation-test-id';
export const ALLOW_PAT_GROUP_SITE_SETTING_TEST_ID = 'allow-pat-group-test-id';
export const ALLOW_PAT_GROUP_ALL_RADIO_BUTTON_TEST_ID = 'allow-pat-group-all-radio-button-test-id';
export const ALLOW_PAT_GROUP_SPECIFIC_RADIO_BUTTON_TEST_ID = 'allow-pat-group-specific-radio-button-test-id';
export const ALLOW_PAT_GROUP_DELETED_WARNING_TEST_ID = 'allow-pat-group-deleted-warning-test-id';
export const PAT_EXPIRY_RADIO_BUTTON_DEFAULT_TESTID = 'pat-expiry-default-radio-button-test-id';
export const PAT_EXPIRY_RADIO_BUTTON_CUSTOM_TESTID = 'pat-expiry-custom-radio-button-test-id';
export const PAT_EXPIRY_TEXTBOX_CUSTOM_TESTID = 'pat-expiry-custom-textbox-test-id';
export const PAT_EXPIRY_RADIO_BUTTION_GROUP_TESTID = 'pat-expiry-radio-button-group-test-id';

export type AllowPatCreationSectionProps = SiteSettingsForSiteAdminProps<'allowPatCreation' | 'allowPatGroup' | 'patAbsoluteExpiry'> & { allowPatGroupButtonSelected?: boolean };

export function AllowPatCreationSection(props: AllowPatCreationSectionProps): React.ReactElement {
  const formik = useFormikContext<AllowPatCreationSectionProps>();
  setUpInitialButtonStates(formik);

  return useShowSettingsSection() && (
    <SettingsSection title={Messages.allowPatCreationTitle()}
      testId={'allow-pat-creation-section'}
      description={
        <div>
          {Messages.allowPatCreationDetails()}
          &nbsp;
          <TextLinkWidget
            testId={'allow-pat-creation-description'}
            target={'_blank'}
            className={learnMoreStyle}
            href={HelpService.getServerHelpUrlByCategory('personal-access-token')}>
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
      }>
      <div className={SettingsStyles.checkboxGroup}>
        {AllowPatCreationCheckbox(formik)}
        {AllowPatGroupRadioGroupAndDropDown(formik)}
        {PatAbsoluteExpiryRadioGroup(formik)}
      </div>
    </SettingsSection>
  );
}

function setUpInitialButtonStates(formik: FormikContextType<AllowPatCreationSectionProps>) {
  // Set up some additional states around button click so that we can block Save
  if (formik.values.allowPatGroupButtonSelected === undefined) {
    formik.resetForm({values: {...formik.values, allowPatGroupButtonSelected: formik.values.settings.allowPatGroup !== undefined}});
  }
}

function useShowSettingsSection(): boolean {
  const isOnlineEnvironment = useSelector(SessionServerSelectors.isOnlineEnvironment);
  const isSiteAdmin = useSelector(SessionSiteSelectors.isSessionSiteAdmin);

  return FeatureFlags.patCreationControlEnabled && isOnlineEnvironment && isSiteAdmin;
}

async function getGroups(): Promise<IGroupInfo[]> {
  let allGroups: IGroupInfo[] = [];
  let moreItems: boolean = true;
  let startIndex: number = 0;
  const GET_GROUPS_PAGE_SIZE = 600;

  while (moreItems) {
    const pagedGroups = await Groups.all().getSlice(startIndex, GET_GROUPS_PAGE_SIZE);
    allGroups = allGroups.concat(pagedGroups.items);
    startIndex = startIndex + GET_GROUPS_PAGE_SIZE;
    moreItems = (startIndex < pagedGroups.total);
  }
  return allGroups;
}

function AllowPatCreationCheckbox(formik: FormikContextType<AllowPatCreationSectionProps>): JSX.Element {
  const [checked, setChecked] = React.useState<boolean>(formik.values.settings.allowPatCreation);

  function handleCheckboxClick(){
    setChecked(!checked);
    // ResetForm() to block save
    if (formik.values.allowPatGroupButtonSelected && formik.values.settings.allowPatGroup === undefined) {
      formik.resetForm({values: {...formik.values, settings: {...formik.values.settings, allowPatCreation: checked? false : true}}});
      return;
    }
    formik.setFieldValue(ALLOW_PAT_CREATION_SITE_SETTING, !checked);
  }

  return (
    <div className={classes(SettingsStyles.checkboxGroup)}>
      <FormikCheckbox
        name={ALLOW_PAT_CREATION_SITE_SETTING}
        label={Messages.allowPatCreationCheckBox()}
        testId={ALLOW_PAT_CREATION_SITE_SETTING_TEST_ID}
        onChange={handleCheckboxClick}
        checked={checked}
      />
    </div>
  );
}

function AllowPatGroupRadioGroupAndDropDown(formik: FormikContextType<AllowPatCreationSectionProps>): JSX.Element {
  const buttons = React.useMemo(() => getAllowGroupButtonsList(formik), [formik]);
  const [allowGroupRadioButtonSelected, setAllowGroupRadioButtonSelected] = React.useState<boolean>(formik.values.settings.allowPatGroup !== undefined);
  const [allowPatGroup, setAllowPatGroup] = React.useState<string>(formik.values.settings.allowPatGroup);
  const initRadioButton = React.useRef<string>(formik.values.settings.allowPatGroup);

  function handleButtonChange(value: string) {
    // We only allow Save when 
    // 1. value === undefined: this is when user clicks on the Allow All Users option
    // 2. allowPatGroup !== undefined, meaning that allowPatGroup is a real value not "No group selected"
    if (value === undefined || allowPatGroup !== undefined) {
      formik.setFieldTouched(ALLOW_PAT_GROUP_SITE_SETTING, initRadioButton.current !== value);
      formik.setFieldValue(ALLOW_PAT_GROUP_SITE_SETTING, value === undefined ? value : allowPatGroup);
      formik.setFieldValue('allowPatGroupButtonSelected', !formik.values.allowPatGroupButtonSelected);
    } else {
      // ResetForm() as a way to block Save
      formik.resetForm({values: {...formik.values, allowPatGroupButtonSelected: !formik.values.allowPatGroupButtonSelected}});
    }
    setAllowGroupRadioButtonSelected(!allowGroupRadioButtonSelected);
  }

  function handleDropdownChange(value: string) {
    if (value === Messages.personalAccessTokenAllowGroupNonSelected()) {
      return;
    }
    formik.setFieldValue(ALLOW_PAT_GROUP_SITE_SETTING, value);
    setAllowPatGroup(value);
  }

  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <div className={subSettingsWrapper}>
        <RadioButtonWithLabelGroupWidget
          buttonList={buttons}
          className={settingsRadioButtonItemHeight}
          removeLeftMargin
          name={ALLOW_PAT_GROUP_SITE_SETTING}
          testId={ALLOW_PAT_GROUP_SITE_SETTING_TEST_ID}
          checkedIndex={allowGroupRadioButtonSelected ? 1 : 0}
          handleChange={(value: string) => handleButtonChange(value)}
          label={Messages.allowPatCreationSetAccess()}
        />
      </div>
      <div>
        {AllowPatCreationGroupDropdown(formik, allowGroupRadioButtonSelected, handleDropdownChange, allowPatGroup)}
      </div>
    </div>
  );
}

function getAllowGroupButtonsList(formik: FormikContextType<AllowPatCreationSectionProps>): RadioButtonWithLabelOption<String>[] {
  // When the All Users option is clicked, we return undefined to distinguish it from when the second Only Groups option where we return an empty string
  return [
    {
      value: ALLOW_PAT_DEFAULT_GROUP,
      label: Messages.allowPatGroupForAll(),
      testId: ALLOW_PAT_GROUP_ALL_RADIO_BUTTON_TEST_ID,
      disabled: !formik.values.settings.allowPatCreation
    },
    {
      value: '',
      label: Messages.allowPatGroupSpecificGroup(),
      testId: ALLOW_PAT_GROUP_SPECIFIC_RADIO_BUTTON_TEST_ID,
      disabled: !formik.values.settings.allowPatCreation
    }
  ];
}

function AllowPatCreationGroupDropdown(formik: FormikContextType<AllowPatCreationSectionProps>, allowGroupRadioButtonSelected: boolean, handleDropdownChange: any,
  allowPatGroup: string): JSX.Element {
  const [groups, setGroups] = React.useState<IGroupInfo[]>([]);

  React.useEffect(() => {
    const getAllGroups = async () => {
      const groups = await getGroups();
      setGroups(groups);
    };

    getAllGroups();
  }, []);

  let previousGroupDeleted = false;

  function getAllowPatGroup(allowPatGroupLuid: string): string {
    if (!allowPatGroupLuid) return Messages.personalAccessTokenAllowGroupSelectGroup();

    if (groups.find(g => g.groupLuid === allowPatGroupLuid)) {
      return allowPatGroupLuid;
    } else {
      // If the groupLuid is not undefined but cannot match any of the exiting group, it means that it's an orphaned value. Display the warning message
      previousGroupDeleted = true;
      return Messages.personalAccessTokenAllowGroupNonSelected();
    }
  }

  const groupListItems = groups.map(group => ({
    // Content is limited to 50 char length due to it making the UI look weird otherwise
    content: L10n.elideIfLong(group.name, 50),
    tooltip: group.name,
    value: group.groupLuid
  }));

  // By default the filtering list would filter on value (project ID), but we want to filter on name
  // We use the tooltip instead of content because the content string may be elided if the name is long
  const groupFilterFn = (item: ListItem, filter: string) => item.tooltip?.toLowerCase().includes(filter.toLowerCase());

  let allowPatGroupLuid = getAllowPatGroup(allowPatGroup);
  if (!allowPatGroupLuid) {
    allowPatGroupLuid = Messages.personalAccessTokenAllowGroupNonSelected();
  }

  return <div className={SettingsStyles.settingsSubGroup}>
    <SingleSelectDropdown
      list={<LocallyFilteringList items={groupListItems} itemFilterFn={groupFilterFn} className={dropdownListStyle} />}
      type={DropdownButtonType.Outline}
      onChange={(value: string) => handleDropdownChange(value)}
      value={allowPatGroupLuid}
      label=''
      disabled={!formik.values.settings.allowPatCreation || !allowGroupRadioButtonSelected}
    />
    {previousGroupDeleted && <InlineAlertWidget testId={ALLOW_PAT_GROUP_DELETED_WARNING_TEST_ID} type={InlineAlertType.Warning} style={{ marginBottom: 10 }}>
      {Messages.personalAccessTokenAllowGroupDeleted()}
    </InlineAlertWidget>}
  </div>;
}

export function PatAbsoluteExpiryRadioGroup(formik: FormikContextType<AllowPatCreationSectionProps>) {
  const initRadioButton = React.useRef<boolean>(formik.values.settings.patAbsoluteExpiry === PAT_DEFAULT_EXPIRY);
  const [isDefaultButtonClicked, setIsDefaultButtonclicked] = React.useState<boolean>(formik.values.settings.patAbsoluteExpiry === PAT_DEFAULT_EXPIRY);
  const buttons = React.useMemo(() => getButtonsList(formik, isDefaultButtonClicked), [formik, isDefaultButtonClicked]);

  function handleButtonClick(value: number, checkedIndex: number) {
    setIsDefaultButtonclicked(!isDefaultButtonClicked);
    // ResetForm() as a way to block Save
    if (formik.values.allowPatGroupButtonSelected && !formik.values.settings.allowPatGroup) {
      formik.resetForm({values: {...formik.values, settings: {...formik.values.settings, patAbsoluteExpiry: checkedIndex === 0 ? PAT_DEFAULT_EXPIRY : PAT_EXPIRY_MIN_RANGE_VALUE}}});
      return;
    }
    if (checkedIndex === 0) {
      formik.setFieldValue(PAT_ABSOLUTE_EXPIRY_SITE_SETTING, PAT_DEFAULT_EXPIRY);
    } else {
      formik.setFieldValue(PAT_ABSOLUTE_EXPIRY_SITE_SETTING, PAT_EXPIRY_MIN_RANGE_VALUE);
    }
    formik.setFieldTouched(PAT_ABSOLUTE_EXPIRY_SITE_SETTING, initRadioButton.current);
  }

  return (
    <div className={SettingsStyles.settingsSubGroup}>
      <div className={subSettingsWrapper}>
        <RadioButtonWithLabelGroupWidget
          buttonList={buttons}
          className={settingsRadioButtonItemHeight}
          removeLeftMargin
          name={PAT_ABSOLUTE_EXPIRY_SITE_SETTING}
          label={Messages.setPatExpiration()}
          testId={PAT_EXPIRY_RADIO_BUTTION_GROUP_TESTID}
          checkedIndex={isDefaultButtonClicked ? 0 : 1}
          handleChange={(value: number, checkedIndex: number) => handleButtonClick(value, checkedIndex)}
        />
      </div>
    </div>
  );
}

function getButtonsList(formik: FormikContextType<AllowPatCreationSectionProps>, isDefaultButtonClicked: boolean
): RadioButtonWithLabelOption<number>[] {
  return [
    {
      value: PAT_DEFAULT_EXPIRY,
      label: Messages.patExpirationDefault(),
      testId: PAT_EXPIRY_RADIO_BUTTON_DEFAULT_TESTID,
      disabled: !formik.values.settings.allowPatCreation,
    },
    {
      value: formik.values.settings.patAbsoluteExpiry,
      label: Messages.patExpirationSpecificValue(),
      detail: CustomPatExpiryOption(formik, isDefaultButtonClicked),
      testId: PAT_EXPIRY_RADIO_BUTTON_CUSTOM_TESTID,
      disabled: !formik.values.settings.allowPatCreation
    }
  ];
}

export function CustomPatExpiryOption(formik: FormikContextType<AllowPatCreationSectionProps>, isDefaultButtonClicked: boolean) {
  function patExpiryValidator(value: string): string | undefined {
    if (+value > PAT_EXPIRY_MAX_RANGE_VALUE || +value < PAT_EXPIRY_MIN_RANGE_VALUE) {
      return Messages.patExpiryRangeInvalid();
    }
  }
  return (
    <FormikTextBox
      name={PAT_ABSOLUTE_EXPIRY_SITE_SETTING}
      label={''}
      aria-label={Messages.patExpirationSpecificValue()}
      testId={PAT_EXPIRY_TEXTBOX_CUSTOM_TESTID}
      maxLength={4}
      min={PAT_EXPIRY_MIN_RANGE_VALUE}
      type={'number'}
      eagerValidation={true}
      onChange={e => formik.setFieldValue(PAT_ABSOLUTE_EXPIRY_SITE_SETTING, +e.target.value)}
      disabled={!formik.values.settings.allowPatCreation || isDefaultButtonClicked}
      defaultValue={isDefaultButtonClicked ? '' : formik.initialValues.settings.patAbsoluteExpiry + ''}
      value={isDefaultButtonClicked ? '' : formik.values.settings.patAbsoluteExpiry + ''}
      validate={patExpiryValidator}
    />
  );
}

/* for registering the component in settings registry */
export const AllowPatCreationSpecs = componentRegistryEntry({
  name: 'AllowPatCreationSection',
  component: AllowPatCreationSection
});